/**
 * `office:text` body parsing and surgical paragraph rewriting.
 *
 * The output is deliberately the same shape the Word reader produces —
 * {@link DocxBlock}, {@link DocxParagraph}, {@link DocxRun}. Those types
 * describe paragraphs, runs and tables, not anything Word-specific, and a
 * writer's document is made of the same three things. Reusing them means the
 * document viewer, the search, the outline and the plain-text export all work
 * on an ODT the day the parser lands, with no new UI and no second rendering
 * path to keep in step.
 *
 * The save path is the part-preserving one: each paragraph keeps the byte span
 * it occupies in `content.xml`, an edit replaces exactly that span, and every
 * other part of the package — `styles.xml`, `meta.xml`, `settings.xml`,
 * pictures, embedded objects — is copied through untouched (§13, §24).
 *
 * Two ODF-specific traps shape the code:
 *
 * - **Whitespace collapses.** ODF folds runs of spaces the way HTML does, so
 *   text with two spaces in it must be written back as a space plus
 *   `<text:s text:c="1"/>`. Writing the literal spaces would silently delete
 *   them on the next open.
 * - **A comment lives inside the paragraph it annotates.** `office:annotation`
 *   holds `text:p` elements of its own. A scanner that did not track depth
 *   would read a comment as body text, and a rebuild that ignored it would
 *   delete the comment. Both are handled: the subtree is skipped for text, and
 *   the paragraph is marked not editable so the rebuild never happens.
 */
import type { DocxBlock, DocxParagraph, DocxRun, DocxTableCell } from '../docx/document-xml.js';
import { escapeXml } from '../ooxml/xml.js';
import { HandlerError } from '../contract.js';
import { attr, childElements, findElement, intAttr, lengthToEmu, tokenize } from './scan.js';
import type { ElementSpan } from './scan.js';
import { headingLevel, resolveParagraph, resolveText } from './styles.js';
import type { OdfStyleTable } from './styles.js';
import { resolveHref } from './package.js';

export interface OdfTextParseOptions {
  readonly styles: OdfStyleTable;
  readonly maxBlocks: number;
}

export interface ParsedOdfText {
  readonly blocks: readonly DocxBlock[];
  readonly paragraphs: readonly DocxParagraph[];
  readonly truncated: boolean;
  /** Picture parts referenced by the body, in first-use order. */
  readonly imageParts: readonly string[];
  /** Hrefs that pointed outside the package and were not resolved (§29). */
  readonly externalRefs: readonly string[];
}

/**
 * Elements whose contents are not body text and whose presence means the
 * paragraph cannot be rebuilt from a string without losing something.
 */
const OPAQUE = new Set([
  'office:annotation', 'office:annotation-end',
  'text:note', 'text:ruby',
  'draw:frame', 'draw:g', 'draw:custom-shape', 'draw:line', 'draw:rect',
  'draw:circle', 'draw:ellipse', 'draw:polygon', 'draw:polyline', 'draw:path',
  'text:change', 'text:change-start', 'text:change-end',
  'text:bookmark', 'text:bookmark-start', 'text:bookmark-end',
  'text:reference-mark', 'text:reference-mark-start', 'text:reference-mark-end',
  'text:toc-mark', 'text:toc-mark-start', 'text:toc-mark-end',
  'text:alphabetical-index-mark', 'text:alphabetical-index-mark-start',
]);

/** Field elements: they render as text but their value is computed. */
const FIELDS = new Set([
  'text:page-number', 'text:page-count', 'text:date', 'text:time',
  'text:title', 'text:subject', 'text:description', 'text:keywords',
  'text:author-name', 'text:author-initials', 'text:file-name',
  'text:chapter', 'text:sequence', 'text:variable-get', 'text:variable-set',
  'text:user-defined', 'text:bookmark-ref', 'text:reference-ref',
  'text:sheet-name', 'text:creation-date', 'text:modification-date',
  'text:editing-cycles', 'text:page-continuation', 'text:placeholder',
]);

/** Present but carrying no content — safe to drop when rewriting. */
const IGNORABLE = new Set([
  'text:soft-page-break', 'office:forms', 'text:sequence-decls',
  'text:sequence-decl', 'text:tracked-changes', 'style:style',
]);

/* ------------------------------------------------------------------ *
 * paragraphs
 * ------------------------------------------------------------------ */

interface RunScan {
  readonly runs: DocxRun[];
  readonly editable: boolean;
  readonly imageParts: string[];
  readonly externalRefs: string[];
}

function scanRuns(
  xml: string, span: ElementSpan, options: OdfTextParseOptions,
): RunScan {
  const runs: DocxRun[] = [];
  const imageParts: string[] = [];
  const externalRefs: string[] = [];
  let editable = true;

  const paragraphStyle = attr(span.attrs, 'text:style-name');
  /** Innermost last. The paragraph's own style is always the outermost link. */
  const styleStack: (string | null)[] = [paragraphStyle];
  const hrefStack: string[] = [];

  /** Non-empty while inside a subtree whose text is not body text. */
  let skipping: { name: string; depth: number } | null = null;

  const push = (text: string): void => {
    if (!text) return;
    const style = resolveText(options.styles, styleStack);
    runs.push({
      kind: 'text',
      text,
      bold: style.bold,
      italic: style.italic,
      underline: style.underline,
      strike: style.strike,
      vertical: style.vertical,
      highlight: style.highlight,
      color: style.color,
      href: hrefStack.length > 0 ? hrefStack[hrefStack.length - 1]! : null,
      revision: null,
    });
  };

  for (const token of tokenize(xml, span.innerStart, span.innerEnd)) {
    if (skipping) {
      if (token.kind === 'open' && token.name === skipping.name) skipping.depth += 1;
      else if (token.kind === 'close' && token.name === skipping.name) {
        skipping.depth -= 1;
        if (skipping.depth === 0) skipping = null;
      }
      continue;
    }

    switch (token.kind) {
      case 'text':
        push(token.text);
        break;

      case 'open':
      case 'empty': {
        const name = token.name;

        if (name === 'text:span') {
          if (token.kind === 'open') styleStack.push(attr(token.attrs, 'text:style-name'));
          break;
        }
        if (name === 'text:a') {
          const href = attr(token.attrs, 'xlink:href') ?? '';
          if (token.kind === 'open') hrefStack.push(href);
          break;
        }
        if (name === 'text:s') {
          // `text:c` counts *additional* spaces beyond the one the element
          // itself stands for, and defaults to 1 meaning a single space.
          push(' '.repeat(Math.max(1, intAttr(token.attrs, 'text:c', 1))));
          break;
        }
        if (name === 'text:tab') { runs.push({ kind: 'tab' }); break; }
        if (name === 'text:line-break') { runs.push({ kind: 'break' }); break; }

        if (name === 'draw:frame' || name === 'draw:image') {
          const picture = readPicture(xml, token, name);
          if (picture) {
            if (picture.part) imageParts.push(picture.part);
            else if (picture.href) externalRefs.push(picture.href);
            runs.push(picture.run);
          }
          editable = false;
          if (token.kind === 'open') skipping = { name, depth: 1 };
          break;
        }

        if (FIELDS.has(name)) {
          editable = false;
          if (token.kind === 'open') {
            // The element's own text is the last computed result, which is what
            // the writer last displayed and the only value available here (§15
            // takes the same position for spreadsheet formulas).
            const inner = readInnerText(xml, token.start);
            runs.push({ kind: 'field', instruction: name, text: inner });
            skipping = { name, depth: 1 };
          } else {
            runs.push({ kind: 'field', instruction: name, text: '' });
          }
          break;
        }

        if (OPAQUE.has(name)) {
          editable = false;
          if (token.kind === 'open') skipping = { name, depth: 1 };
          break;
        }

        if (IGNORABLE.has(name)) break;

        // Anything unrecognised is treated as a container: its text still
        // belongs to the paragraph, but the paragraph can no longer be safely
        // rebuilt, because rebuilding would drop the element (§19).
        if (token.kind === 'open') editable = false;
        break;
      }

      case 'close':
        if (token.name === 'text:span') styleStack.pop();
        else if (token.name === 'text:a') hrefStack.pop();
        break;
    }
  }

  return { runs, editable, imageParts, externalRefs };
}

interface Picture {
  readonly run: DocxRun;
  readonly part: string | null;
  readonly href: string | null;
}

function readPicture(
  xml: string, token: { start: number; end: number; attrs: string }, name: string,
): Picture | null {
  // A `draw:frame` carries the geometry and wraps a `draw:image`; a bare
  // `draw:image` carries the href alone. Both forms occur.
  const scope = name === 'draw:frame'
    ? xml.slice(token.start, endOfSubtree(xml, token, name))
    : xml.slice(token.start, token.end);

  const hrefMatch = /<draw:image\b[^>]*\bxlink:href\s*=\s*"([^"]*)"/.exec(scope)
    ?? (name === 'draw:image' ? /\bxlink:href\s*=\s*"([^"]*)"/.exec(scope) : null);
  const href = hrefMatch ? hrefMatch[1]! : null;
  const part = resolveHref(href);

  const alt = /<svg:title\b[^>]*>([\s\S]*?)<\/svg:title>/.exec(scope)?.[1]
    ?? /<svg:desc\b[^>]*>([\s\S]*?)<\/svg:desc>/.exec(scope)?.[1]
    ?? attr(token.attrs, 'draw:name')
    ?? '';

  return {
    run: {
      kind: 'image',
      // ODF has no relationship ids; the href *is* the identity, so it plays
      // the same role and the viewer needs no special case.
      relId: href ?? '',
      part,
      widthEmu: lengthToEmu(attr(token.attrs, 'svg:width')) ?? 0,
      heightEmu: lengthToEmu(attr(token.attrs, 'svg:height')) ?? 0,
      alt: alt.trim(),
    },
    part,
    href: part ? null : href,
  };
}

function endOfSubtree(
  xml: string, token: { start: number; end: number }, name: string,
): number {
  let depth = 0;
  for (const t of tokenize(xml, token.start)) {
    if (t.name !== name) continue;
    if (t.kind === 'empty' && t.start === token.start) return t.end;
    if (t.kind === 'open') depth += 1;
    else if (t.kind === 'close') {
      depth -= 1;
      if (depth === 0) return t.end;
    }
  }
  return xml.length;
}

function readInnerText(xml: string, start: number): string {
  const open = xml.indexOf('>', start);
  if (open < 0) return '';
  let depth = 1;
  let out = '';
  for (const token of tokenize(xml, open + 1)) {
    if (token.kind === 'text') { out += token.text; continue; }
    if (token.kind === 'open') depth += 1;
    else if (token.kind === 'close') { depth -= 1; if (depth === 0) break; }
  }
  return out;
}

function runsToText(runs: readonly DocxRun[]): string {
  return runs.map((run) => {
    switch (run.kind) {
      case 'text': return run.text;
      case 'tab': return '\t';
      case 'break': return '\n';
      case 'field': return run.text;
      default: return '';
    }
  }).join('');
}

interface ParagraphContext {
  readonly listLevel: number | null;
  readonly listOrdered: boolean;
}

function buildParagraph(
  xml: string, span: ElementSpan, index: number,
  options: OdfTextParseOptions, context: ParagraphContext,
  imageParts: string[], externalRefs: string[],
): DocxParagraph {
  const scan = scanRuns(xml, span, options);
  imageParts.push(...scan.imageParts);
  externalRefs.push(...scan.externalRefs);

  const styleName = attr(span.attrs, 'text:style-name');
  const paragraph = resolveParagraph(options.styles, styleName);
  const text = runsToText(scan.runs);

  return {
    kind: 'paragraph',
    index,
    styleId: styleName,
    styleName: styleName ? options.styles.styles.get(styleName)?.displayName ?? styleName : null,
    headingLevel: headingLevel(
      options.styles, styleName,
      span.name === 'text:h' ? attr(span.attrs, 'text:outline-level') : null,
    ),
    alignment: paragraph.alignment,
    listLevel: context.listLevel,
    listOrdered: context.listOrdered,
    runs: scan.runs,
    text,
    originalText: text,
    editable: scan.editable,
    start: span.start,
    end: span.end,
  };
}

/* ------------------------------------------------------------------ *
 * body
 * ------------------------------------------------------------------ */

/**
 * Parse the text body into blocks.
 *
 * The walk is recursive because ODF nests structure that Word keeps flat:
 * lists hold list items which hold paragraphs *and* further lists, sections
 * hold anything, and a table cell holds the lot again. Recursion is bounded by
 * {@link MAX_NESTING} — a hand-built file can nest arbitrarily, and a stack
 * overflow is a worse failure than a truncated document (§30).
 */
export function parseOdfText(
  xml: string, options: OdfTextParseOptions,
): ParsedOdfText {
  const body = findBody(xml);
  const blocks: DocxBlock[] = [];
  const paragraphs: DocxParagraph[] = [];
  const imageParts: string[] = [];
  const externalRefs: string[] = [];
  let truncated = false;

  if (!body) {
    return { blocks, paragraphs, truncated: false, imageParts, externalRefs };
  }

  const walk = (parent: ElementSpan, context: ParagraphContext, depth: number): void => {
    if (truncated || depth > MAX_NESTING) return;

    for (const child of childElements(xml, parent, STRUCTURE)) {
      if (blocks.length >= options.maxBlocks) { truncated = true; return; }

      switch (child.name) {
        case 'text:p':
        case 'text:h': {
          const paragraph = buildParagraph(
            xml, child, paragraphs.length, options, context, imageParts, externalRefs,
          );
          paragraphs.push(paragraph);
          blocks.push(paragraph);
          break;
        }

        case 'table:table':
          blocks.push(readTable(xml, child, options, paragraphs, imageParts, externalRefs, depth));
          break;

        case 'text:list': {
          const styleName = attr(child.attrs, 'text:style-name');
          const ordered = styleName !== null
            ? options.styles.orderedLists.get(styleName) ?? context.listOrdered
            : context.listOrdered;
          walk(child, {
            listLevel: (context.listLevel ?? -1) + 1,
            listOrdered: ordered,
          }, depth + 1);
          break;
        }

        default:
          // `text:list-item`, `text:section`, `text:index-body` and friends are
          // grouping elements: their children are the content.
          walk(child, context, depth + 1);
          break;
      }
    }
  };

  walk(body, { listLevel: null, listOrdered: false }, 0);
  return { blocks, paragraphs, truncated, imageParts, externalRefs };
}

const MAX_NESTING = 24;

/** Elements the body walk descends into or turns into blocks. */
const STRUCTURE = [
  'text:p', 'text:h', 'table:table', 'text:list', 'text:list-item',
  'text:list-header', 'text:section', 'text:index-body', 'text:index-title',
  'office:forms', 'text:table-of-content', 'text:alphabetical-index',
  'text:illustration-index', 'text:table-index', 'text:user-index',
  'text:bibliography', 'draw:frame', 'text:tracked-changes',
];

function findBody(xml: string): ElementSpan | null {
  // `office:body` is nested inside `office:document-content`, so it is found by
  // depth-aware search rather than as a top-level child.
  const body = findElement(xml, 'office:body');
  if (!body) return null;
  return childElements(xml, body, ['office:text'])[0] ?? null;
}

function readTable(
  xml: string, span: ElementSpan, options: OdfTextParseOptions,
  paragraphs: DocxParagraph[], imageParts: string[], externalRefs: string[],
  depth: number,
): DocxBlock {
  const rows: DocxTableCell[][] = [];

  for (const rowSpan of tableRows(xml, span)) {
    const repeat = Math.min(intAttr(rowSpan.attrs, 'table:number-rows-repeated', 1), 64);
    const cells: DocxTableCell[] = [];

    for (const cellSpan of childElements(xml, rowSpan, ['table:table-cell', 'table:covered-table-cell'])) {
      const covered = cellSpan.name === 'table:covered-table-cell';
      const cellRepeat = Math.min(intAttr(cellSpan.attrs, 'table:number-columns-repeated', 1), 64);
      const cellParagraphs: DocxParagraph[] = [];

      if (!covered && depth < MAX_NESTING) {
        for (const child of childElements(xml, cellSpan, ['text:p', 'text:h'])) {
          const paragraph = buildParagraph(
            xml, child, paragraphs.length, options,
            { listLevel: null, listOrdered: false }, imageParts, externalRefs,
          );
          paragraphs.push(paragraph);
          cellParagraphs.push(paragraph);
        }
      }

      const cell: DocxTableCell = {
        paragraphs: cellParagraphs,
        colSpan: intAttr(cellSpan.attrs, 'table:number-columns-spanned', 1),
        merged: covered,
      };
      // A repeated cell is one element standing for several identical columns.
      // The repeats after the first share the parsed paragraphs, which is the
      // truth: they are the same content, and only the first is editable.
      cells.push(cell);
      for (let i = 1; i < cellRepeat; i++) {
        cells.push({ paragraphs: [], colSpan: cell.colSpan, merged: cell.merged });
      }
    }

    for (let i = 0; i < repeat; i++) rows.push(cells);
  }

  return { kind: 'table', rows, start: span.start, end: span.end };
}

/** Rows, descending through the header/group wrappers ODF permits. */
function* tableRows(xml: string, span: ElementSpan): Generator<ElementSpan> {
  const groups = [
    'table:table-row', 'table:table-header-rows', 'table:table-rows',
    'table:table-row-group',
  ];
  for (const child of childElements(xml, span, groups)) {
    if (child.name === 'table:table-row') yield child;
    else yield* tableRows(xml, child);
  }
}

/* ------------------------------------------------------------------ *
 * editing
 * ------------------------------------------------------------------ */

export interface OdfTextEdit {
  readonly index: number;
  readonly text: string;
}

export interface OdfTextEditResult {
  readonly xml: string;
  readonly dropped: readonly string[];
  readonly failed: readonly number[];
}

/**
 * Rewrite the text of specific paragraphs inside `content.xml`.
 *
 * The opening tag is kept verbatim, so `text:style-name` and
 * `text:outline-level` survive exactly. The body is replaced with plain
 * character data — which means a paragraph whose runs carried *different*
 * styles comes back in one style, and that is reported as a dropped feature
 * rather than passed off as a clean save (§13, §24).
 *
 * Edits are applied back to front so that earlier spans keep their offsets.
 */
export function applyOdfTextEdits(
  xml: string, paragraphs: readonly DocxParagraph[], edits: readonly OdfTextEdit[],
): OdfTextEditResult {
  const byIndex = new Map(paragraphs.map((p) => [p.index, p]));
  const dropped: string[] = [];
  const failed: number[] = [];

  const ordered = [...edits].sort((a, b) =>
    (byIndex.get(b.index)?.start ?? -1) - (byIndex.get(a.index)?.start ?? -1));

  let out = xml;
  for (const edit of ordered) {
    const paragraph = byIndex.get(edit.index);
    if (!paragraph || !paragraph.editable) { failed.push(edit.index); continue; }

    const source = out.slice(paragraph.start, paragraph.end);
    const openEnd = source.indexOf('>');
    if (openEnd < 0 || !source.startsWith('<text:')) { failed.push(edit.index); continue; }

    const name = /^<([\w:.-]+)/.exec(source)?.[1];
    if (name !== 'text:p' && name !== 'text:h') { failed.push(edit.index); continue; }

    const styleCount = new Set(paragraph.runs
      .filter((r): r is Extract<DocxRun, { kind: 'text' }> => r.kind === 'text')
      .map((r) => `${r.bold}|${r.italic}|${r.underline}|${r.strike}|${r.color}|${r.href}`)).size;
    if (styleCount > 1) {
      dropped.push(`문단 ${edit.index + 1}: 문단 안의 서로 다른 글자 모양이 하나로 통일됨`);
    }

    const openTag = source.slice(0, openEnd + 1).replace(/\s*\/>$/, '>');
    out = out.slice(0, paragraph.start)
      + `${openTag}${encodeOdfText(edit.text)}</${name}>`
      + out.slice(paragraph.end);
  }

  return { xml: out, dropped, failed };
}

/**
 * Encode a string as ODF character data.
 *
 * ODF collapses whitespace the way HTML does, so runs of spaces, tabs and line
 * breaks each need their own element. Writing the literal characters would let
 * them survive one round-trip through this application and then quietly
 * disappear the first time another writer opened the file.
 */
export function encodeOdfText(text: string): string {
  return text.split('\n').map((line, lineIndex) => {
    const parts = line.split('\t').map((segment) => encodeSpaces(segment));
    return (lineIndex === 0 ? '' : '<text:line-break/>') + parts.join('<text:tab/>');
  }).join('');
}

function encodeSpaces(segment: string): string {
  let out = '';
  let i = 0;
  while (i < segment.length) {
    const ch = segment[i]!;
    if (ch !== ' ') {
      const next = segment.indexOf(' ', i);
      const end = next < 0 ? segment.length : next;
      out += escapeXml(segment.slice(i, end));
      i = end;
      continue;
    }
    let run = 0;
    while (i + run < segment.length && segment[i + run] === ' ') run += 1;
    // A leading space would be stripped entirely, so it too becomes an element.
    if (out === '') {
      out += run === 1 ? '<text:s/>' : `<text:s text:c="${run}"/>`;
    } else {
      out += run === 1 ? ' ' : ` <text:s text:c="${run - 1}"/>`;
    }
    i += run;
  }
  return out;
}

/** Thrown-through helper for callers that need the paragraph or an error. */
export function requireParagraph(
  paragraphs: readonly DocxParagraph[], index: number,
): DocxParagraph {
  const paragraph = paragraphs.find((p) => p.index === index);
  if (!paragraph) {
    throw new HandlerError('error.unableToRead', `no paragraph at index ${index}`);
  }
  return paragraph;
}
